/**
 * The analytics event contract.
 *
 * Every event name and every property that may be sent is declared here.
 * Nothing calls posthog.capture with an ad-hoc string, and no property reaches
 * PostHog unless it appears in ALLOWED_PROPERTIES for that event.
 *
 * The allowlist is the PHI guard. Callers hold whole patient and form objects
 * at these call sites, and an allowlist fails closed the way a denylist cannot:
 * a field added to a form object next year is dropped by default rather than
 * shipped by default. Same reasoning as buildUserProperties() in config/posthog.js.
 *
 * Mirrored in mobileApp/utils/analytics.js. Keep the two in step, or web and
 * mobile stop being comparable in the same chart. One deliberate exception:
 * NAVIGATION_BACKTRACKED describes a browser route round trip and has no
 * mobile equivalent yet, so it is web-only on purpose rather than by drift.
 */
import posthog from 'posthog-js';

export const EVENTS = Object.freeze({
  SCREEN_VIEWED: 'screen_viewed',
  NOTE_COMPLETED: 'note_completed',
  DOCUMENT_SIGNED: 'document_signed',
  VISIT_CLOCK_IN: 'visit_clock_in',
  VISIT_CLOCK_OUT: 'visit_clock_out',
  CLAIM_BATCH_SUBMITTED: 'claim_batch_submitted',
  BATCH_VOIDED: 'batch_voided',
  REPORT_RUN: 'report_run',
  ELIGIBILITY_CHECKED: 'eligibility_checked',
  PATIENT_SELECTED: 'patient_selected',
  QUALITY_OVERRIDE_APPLIED: 'quality_override_applied',
  NAVIGATION_BACKTRACKED: 'navigation_backtracked',
});

/** Per-event property allowlist. `platform` is added automatically. */
const ALLOWED_PROPERTIES = Object.freeze({
  [EVENTS.SCREEN_VIEWED]: ['route'],
  [EVENTS.NOTE_COMPLETED]: ['documentType'],
  [EVENTS.DOCUMENT_SIGNED]: ['documentType', 'source'],
  [EVENTS.VISIT_CLOCK_IN]: [],
  [EVENTS.VISIT_CLOCK_OUT]: ['path'],
  [EVENTS.CLAIM_BATCH_SUBMITTED]: ['clearinghouse'],
  [EVENTS.BATCH_VOIDED]: ['claimType'],
  [EVENTS.REPORT_RUN]: ['reportId'],
  [EVENTS.ELIGIBILITY_CHECKED]: ['bulk'],
  [EVENTS.PATIENT_SELECTED]: ['searchTermLength'],
  [EVENTS.QUALITY_OVERRIDE_APPLIED]: ['ruleId'],
  // Route shapes only — both are passed through sanitizeAnalyticsUrl by the
  // caller in config/navigationFriction.js before they reach here.
  [EVENTS.NAVIGATION_BACKTRACKED]: ['fromRoute', 'toRoute', 'seconds'],
});

const PLATFORM = 'web';

/**
 * Reduce caller-supplied properties to the declared allowlist.
 * @throws when the event name is not declared
 */
export function buildEventProperties(eventName, properties = {}) {
  const allowed = ALLOWED_PROPERTIES[eventName];
  if (!allowed) throw new Error(`Unknown analytics event: ${eventName}`);

  const out = { platform: PLATFORM };
  for (const key of allowed) {
    const value = properties?.[key];
    // Primitives only. An object or array could carry anything.
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Capture an event. Never throws: analytics must not be able to break a
 * clinical submit handler, which is where most of these call sites live.
 */
export function captureEvent(eventName, properties = {}) {
  try {
    posthog.capture(eventName, buildEventProperties(eventName, properties));
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[analytics] capture failed:', err.message);
  }
}
